import {
  Box,
  Button,
  Heading,
  ProgressRoot,
  Span,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { ProgressBar } from "../ui/progress";

export type TipType =
  | "intial-instructions"
  | "suggest-origin"
  | "suggest-hint"
  | "info-instructions"
  | "game-error";

interface TipOverlayProps {
  visible: boolean;
  type: TipType;
  suggestedAirports: string[];
  onClick: () => void;
  duration?: number;
}

export const TipOverlay = ({
  visible,
  type,
  suggestedAirports,
  onClick,
  duration,
}: TipOverlayProps) => {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    setOpen(visible);
    if (visible && duration) {
      setProgress(100);
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev <= 0) {
            clearInterval(interval);
            setOpen(false);
            return 0;
          }
          return prev - 1;
        });
      }, duration / 100);

      return () => clearInterval(interval);
    }
  }, [visible, type]);

  return (
    <>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor="rgba(0, 0, 0, 0.5)"
        zIndex={999}
        visibility={{ base: open ? "visible" : "hidden", md: "hidden" }}
        transition="all 0.2s ease-in-out"
        opacity={open ? 1 : 0}
      />
      <Box
        position="fixed"
        zIndex={1000}
        bg="blackAlpha.800"
        color="white"
        width="90%"
        maxWidth={{ base: "90%", md: "600px" }}
        p={6}
        pt={4}
        borderRadius={{ base: "md", md: "0 0 8px 8px" }}
        visibility={open ? "visible" : "hidden"}
        left="50%"
        top={{ base: "50%", md: "0" }}
        transform={{ base: "translate(-50%, -50%)", md: "translate(-50%, 0%)" }}
        overflow="hidden"
        transition="all 0.2s ease-in-out"
        opacity={open ? 1 : 0}
      >
        <ProgressRoot
          value={progress}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            backgroundColor: "white",
          }}
          display={duration ? "block" : "none"}
        >
          <ProgressBar height={"2px"} />
        </ProgressRoot>
        {(type === "intial-instructions" || type === "info-instructions") && (
          <>
            <Heading fontSize="xl">
              {t("game.tipOverlay.intialInstructions.title")}
            </Heading>
            <Text fontFamily={"sans-serif"}>
              <Trans
                i18nKey="game.tipOverlay.intialInstructions.first"
                components={{
                  emphasize: (
                    <Span
                      style={{
                        fontWeight: "bold",
                        textDecoration: "underline",
                        fontStyle: "italic",
                      }}
                    />
                  ),
                }}
              />
            </Text>
            <Text fontFamily={"sans-serif"} mt={4}>
              {t("game.tipOverlay.intialInstructions.second")}
            </Text>
          </>
        )}
        {type === "suggest-origin" && (
          <>
            <Heading fontSize="xl">{t("game.tipOverlay.origin.title")}</Heading>
            <Text fontFamily={"sans-serif"}>
              <Trans
                i18nKey="game.tipOverlay.origin.instruction"
                components={{
                  highlight: (
                    <Span
                      style={{
                        fontWeight: "bold",
                        textDecoration: "underline",
                      }}
                    />
                  ),
                }}
              />
            </Text>
            <Text fontFamily={"sans-serif"} mt={4}>
              {t("game.tipOverlay.origin.suggestedAirports")}
              {suggestedAirports.map((airport) => airport).join(", ")}
            </Text>
          </>
        )}
        {type === "suggest-hint" && (
          <>
            <Heading fontSize="xl">{t("game.tipOverlay.hint.title")}</Heading>
            <Text fontFamily={"sans-serif"}>
              <Trans
                i18nKey="game.tipOverlay.hint.instruction"
                components={{
                  highlight: (
                    <Span
                      style={{
                        fontWeight: "bold",
                        textDecoration: "underline",
                      }}
                    />
                  ),
                }}
              />
            </Text>
          </>
        )}

        {type === "game-error" && (
          <>
            <Heading fontSize="xl">
              {t("game.tipOverlay.gameError.title")}
            </Heading>
            <Text fontFamily={"sans-serif"} mt={4}>
              {t("game.tipOverlay.gameError.instruction")}
            </Text>
          </>
        )}

        <Button
          mt={4}
          width="100%"
          onClick={onClick}
          backgroundColor={"white"}
          color="black"
          display="block"
        >
          {type === "intial-instructions"
            ? t("game.tipOverlay.intialInstructions.startGuessing")
            : type === "game-error"
            ? t("game.tipOverlay.gameError.button")
            : t("game.tipOverlay.gotIt")}
        </Button>
      </Box>
    </>
  );
};
